import { useEffect, useRef, useState } from 'react'
import { ApiError, api, type Filter, type Job, type Run, type RunPreview } from '../api'
import { FilterBriefPanel } from '../FilterBrief'
import { useRefreshOnActivate } from '../useActive'
import { useNotice } from '../useNotice'

/**
 * People — running a filter, and the saved lists that come out of it.
 *
 * A run scans the CRM, which costs a minute or two whatever the result size:
 * the signal query's metric CTEs evaluate over the whole base regardless of
 * how few rows come back. So the run is a background job with a job id, and
 * this screen polls it rather than pretending a request can hold that long.
 *
 * Preview exists because committing to a list you have not counted is how
 * campaigns end up aimed at the wrong number of people. It does the same scan
 * and writes nothing.
 */
export function PeopleTab({
  active = true,
  preselect,
  onPreselectUsed,
}: {
  active?: boolean
  /** A filter handed over from stage 1, to be preselected in the launcher. */
  preselect?: string | null
  onPreselectUsed?: () => void
  /** Hand a saved list to stage 3. */
}) {
  const [filters, setFilters] = useState<Filter[]>([])
  const [runs, setRuns] = useState<Run[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // One at a time. Two open briefs side by side in a table invite reading a
  // clause from one row as though it belonged to the other.
  const [openRun, setOpenRun] = useState<number | null>(null)

  const load = async () => {
    try {
      setError(null)
      const [f, r] = await Promise.all([api.filters(), api.runs()])
      setFilters(f)
      setRuns(r)
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  // Coming back to this tab picks up runs and filters changed elsewhere. The
  // launcher below is a separate component and keeps its own state, so an
  // in-flight scan is untouched by this.
  useRefreshOnActivate(active, load)

  return (
    <>
      {error && <div className="error">{error}</div>}
      <RunLauncher
        filters={filters}
        preselect={preselect}
        onPreselectUsed={onPreselectUsed}
        onFinished={load}
      />
      <div className="panel">
        <h2>
          Saved lists <span className="count">{runs?.length ?? 0}</span>
        </h2>
        <p className="hint panel-note">
          Each row is who matched a filter on a given day. The list is a standing thing — reopen it,
          add someone, remove someone — so the same set can back a campaign without paying for
          another scan.
        </p>
        {!runs ? (
          <div className="loading">Loading…</div>
        ) : runs.length === 0 ? (
          <div className="empty">No runs yet. Run a filter above.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="num">Run</th>
                <th>Filter</th>
                <th>Date</th>
                <th>Label</th>
                <th className="num">Members</th>
                <th>Scope</th>
                <th>Started by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <RunRow
                  key={r.id}
                  run={r}
                  open={openRun === r.id}
                  onToggle={() => setOpenRun(openRun === r.id ? null : r.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

/**
 * A saved list, and on request the rules that produced it.
 *
 * A run is the thing a campaign is aimed at, so "which people are these"
 * matters more here than anywhere else, and the row carried a filter *name* as
 * the whole answer. The brief is fetched only when opened: it is per-filter,
 * and a table of thirteen would fetch thirteen to show none.
 */
function RunRow({
  run,
  open,
  onToggle,
  onCampaign,
}: {
  run: Run
  open: boolean
  onToggle: () => void
  onCampaign?: (runId: number) => void
}) {
  return (
    <>
      <tr>
        <td className="num mono">{run.id}</td>
        <td className="mono">{run.filter}</td>
        <td>{run.run_date}</td>
        <td>{run.label ?? '—'}</td>
        <td className="num">{run.member_count.toLocaleString()}</td>
        <td>
          {run.scan_limit ? (
            <span
              className="badge parked"
              title={
                `Scanned at most ${run.scan_limit.toLocaleString()} accounts, so this ` +
                `is a sample. A campaign built on it would mail only these members, ` +
                `not everyone the filter matches.`
              }
            >
              sample ≤{run.scan_limit.toLocaleString()}
            </span>
          ) : (
            <span className="badge ok">complete</span>
          )}
        </td>
        <td>{run.triggered_by}</td>
        <td>
          <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
            <button className="linkish" onClick={onToggle}>
              {open ? 'hide rules' : 'what selected these?'}
            </button>
            {/* The stage ends here, and nothing said where it ends up. A list
                with no members cannot back a campaign — createCampaign is
                built against a run that already has people in it — so the
                button states that rather than failing after the click. */}
            {onCampaign && (
              <button
                disabled={run.member_count === 0}
                title={run.member_count === 0 ? 'this list has no members' : undefined}
                onClick={() => onCampaign(run.id)}
              >
                Start a campaign →
              </button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} className="brief-cell">
            <FilterBriefPanel name={run.filter} since={run.created_at} />
          </td>
        </tr>
      )}
    </>
  )
}

function RunLauncher({
  filters,
  preselect,
  onPreselectUsed,
  onFinished,
}: {
  filters: Filter[]
  preselect?: string | null
  onPreselectUsed?: () => void
  onFinished: () => void
}) {
  const runnable = filters.filter((f) => !f.parked && !f.unconditional)
  const [name, setName] = useState('')
  /** Named in the handoff but not runnable — parked, or the catch-all. The
   *  dropdown lists neither, so silently leaving the previous selection in
   *  place would look like the button had done nothing. */
  const [refused, setRefused] = useState<string | null>(null)
  const [label, setLabel] = useState('')
  // 0 = no cap, and the default, because the safe thing should be the default.
  // A pre-filled 500 made the obvious action — pick a filter, Run and save —
  // silently write a 500-member sample that nothing afterwards distinguishes
  // from a complete audience.
  const [limit, setLimit] = useState('0')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<RunPreview | null>(null)
  const [job, setJob] = useState<Job | null>(null)
  // What the scan is for, shown while it runs. A minute of a disabled button
  // and nothing else is indistinguishable from a screen that has hung.
  const [busyWhat, setBusyWhat] = useState<string | null>(null)
  // The handle the running scan can be stopped by: a client token for the
  // synchronous preview, `job:{id}` for the background run. Held in a ref as
  // well as in state because the pollers read it outside a render.
  const [cancelKey, setCancelKey] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [stopped, setStopped] = useNotice(20000)
  // Left open across a change of filter: someone comparing two segments wants
  // the second one explained too, not the panel shut in their face.
  const [showBrief, setShowBrief] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    if (runnable.length && !name) setName(runnable[0].name)
  }, [runnable, name])

  // A filter handed over from stage 1. Consumed once and then cleared by the
  // parent, so coming back to this tab later does not re-select it under
  // somebody who has since picked something else — this dropdown decides who a
  // scan is aimed at.
  useEffect(() => {
    if (!preselect || !filters.length) return
    if (runnable.some((f) => f.name === preselect)) {
      setName(preselect)
      setRefused(null)
    } else {
      setRefused(preselect)
    }
    onPreselectUsed?.()
  }, [preselect, filters])

  // Stop polling when this unmounts, or a job keeps a timer alive forever.
  useEffect(() => () => {
    if (timer.current) window.clearInterval(timer.current)
  }, [])

  // Empty and 0 both mean "no cap"; the API treats 0 and null identically.
  const limitValue = Number(limit.trim()) || 0

  const scope = limitValue ? `capped at ${limitValue.toLocaleString()}` : 'no limit'

  /**
   * Stop whatever is running.
   *
   * One button for both shapes of operation, because from here they are the
   * same act — the difference is only which key identifies the query, and the
   * server takes either. `stopped: false` is not a failure: the scan may have
   * finished in the moment between the button appearing and being pressed,
   * which is worth saying rather than showing an error for.
   */
  const cancel = async () => {
    if (!cancelKey) return
    setCancelling(true)
    try {
      const res = await api.cancelScan(cancelKey)
      if (!res.stopped) setStopped('It had already finished — nothing to stop.')
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setCancelling(false)
    }
  }

  const doPreview = async () => {
    // Generated BEFORE the call, which is the only way a synchronous request
    // can be cancellable: it returns when the scan ends, so a key handed back
    // in the response would arrive too late to be any use.
    const token = api.cancelToken()
    setBusy(true)
    setCancelKey(token)
    setCancelling(false)
    setBusyWhat(`Counting ${name} — ${scope}. Nothing will be saved.`)
    setError(null)
    setPreview(null)
    setStopped(null)
    try {
      setPreview(
        await api.previewRun({ filter: name, limit: limitValue, cancel_token: token }),
      )
    } catch (err) {
      // 499 is this app's own code for "the client asked us to stop". It is an
      // outcome, not a fault, so it must not land in the red error box.
      if (err instanceof ApiError && err.status === 499) {
        setStopped(err.detail)
      } else {
        setError(String((err as Error).message))
      }
    } finally {
      setBusy(false)
      setBusyWhat(null)
      setCancelKey(null)
    }
  }

  const doRun = async () => {
    setBusy(true)
    setBusyWhat(`Scanning for ${name} — ${scope}.`)
    setError(null)
    setPreview(null)
    setJob(null)
    setStopped(null)
    setCancelling(false)
    try {
      const started = await api.startRun({
        filter: name,
        label: label.trim() || undefined,
        limit: limitValue,
      })
      // The job's own id is the handle — the client does not have to invent
      // one, because this endpoint returns before the work is done.
      setCancelKey(`job:${started.job_id}`)
      const poll = async () => {
        const j = await api.job(started.job_id)
        setJob(j)
        if (j.status !== 'running') {
          if (timer.current) window.clearInterval(timer.current)
          timer.current = null
          setBusy(false)
          setBusyWhat(null)
          setCancelKey(null)
          onFinished()
        }
      }
      await poll()
      timer.current = window.setInterval(() => void poll(), 3000)
    } catch (err) {
      setError(String((err as Error).message))
      setBusy(false)
      setBusyWhat(null)
      setCancelKey(null)
    }
  }

  return (
    <div className="panel">
      <h2>Run a filter</h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        {refused && (
          <div className="notice">
            <span className="mono">{refused}</span> cannot be run: it is parked or has no
            condition. Nothing below has changed.
          </div>
        )}

        <div className="row-form">
          <div style={{ minWidth: 320 }}>
            <label htmlFor="f">Filter</label>
            <select id="f" value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }}>
              {runnable.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.title} — {f.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: 200 }}>
            <label htmlFor="l">Label (optional)</label>
            <input id="l" type="text" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div style={{ width: 150 }}>
            <label htmlFor="n">
              Scan limit <span className="label-note">— 0 = none</span>
            </label>
            <input
              id="n"
              type="number"
              min={0}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div className="row">
            <button disabled={busy || !name} onClick={doPreview}>
              Preview count
            </button>
            <button className="primary" disabled={busy || !name} onClick={doRun}>
              {busy ? (
                <span className="row" style={{ gap: 7 }}>
                  <span className="spinner" /> Working…
                </span>
              ) : (
                'Run and save'
              )}
            </button>
          </div>
        </div>

        {stopped && <div className="notice">{stopped}</div>}
        {busyWhat && (
          <BusyNotice
            what={busyWhat}
            cancelling={cancelling}
            onCancel={cancelKey ? () => void cancel() : undefined}
          />
        )}

        <p className="hint">
          Parked and catch-all filters are not listed: neither is sendable. A scan takes a minute or
          more regardless of the limit, because the underlying query aggregates over the whole base
          — so a small limit gives a fast sample, not a faster total, and a run made with one saves
          a partial audience. Keep it at 0 unless you are smoke-testing a filter.
        </p>

        {job && <JobProgress job={job} />}
        {preview && <PreviewResult preview={preview} />}

        {name && (
          <div>
            <button className="linkish" onClick={() => setShowBrief(!showBrief)}>
              {showBrief ? 'Hide what this selects' : 'What does this filter select?'}
            </button>
            {showBrief && <FilterBriefPanel name={name} />}
          </div>
        )}
      </div>
    </div>
  )
}


/**
 * Proof the scan is alive, for however long it takes.
 *
 * There is no progress to report and inventing one would be worse than none:
 * the signal query aggregates over the whole base before the first row is
 * returned, so nothing counts upward until it is finished. What this shows
 * instead is motion plus elapsed time — enough to tell a slow scan from a
 * hung screen, which is the actual question being asked at second forty.
 */
/**
 * A scan in flight, how long it has been going, and how to stop it.
 *
 * The Cancel button lives HERE rather than beside the disabled Run button,
 * because this is the only element on screen that exists exactly as long as
 * the operation does. A scan costs minutes whatever its result size, and the
 * CRM pool is size ONE — so an unwanted scan is not merely a wasted wait, it
 * blocks every other scan until it ends. "Give up and try a smaller limit"
 * was not previously available: the second attempt queued behind the first.
 */
function BusyNotice({
  what,
  onCancel,
  cancelling,
}: {
  what: string
  onCancel?: () => void
  cancelling?: boolean
}) {
  const [seconds, setSeconds] = useState(0)

  useEffect(() => {
    const started = Date.now()
    const id = window.setInterval(
      () => setSeconds(Math.round((Date.now() - started) / 1000)),
      1000,
    )
    return () => window.clearInterval(id)
  }, [])

  return (
    <div className="notice" aria-live="polite" aria-busy="true">
      <div className="row">
        <span className="spinner" />
        <strong>{what}</strong>
        <span className="mono">{seconds}s</span>
        <span style={{ flex: 1 }} />
        {onCancel && (
          <button type="button" className="danger" disabled={cancelling} onClick={onCancel}>
            {cancelling ? 'Stopping…' : 'Cancel'}
          </button>
        )}
      </div>
      <div className="hint caption">
        {seconds < 20
          ? 'Reading the CRM. A minute or two is normal.'
          : 'Still going. The query aggregates over the whole account base before it returns ' +
            'anything, so there is no count to tick upward — leaving this tab does not stop it.'}
        {onCancel && (
          <>
            {' '}
            Cancelling aborts the query at the database, not just this page — which matters,
            because the CRM allows one scan at a time and this one is holding it.
          </>
        )}
      </div>
      <div className="busy-bar" />
    </div>
  )
}

function JobProgress({ job }: { job: Job }) {
  const detail = job.detail as Record<string, unknown>
  // CANCELLED IS AN OUTCOME, NOT A FAILURE, and gets neither the red box nor
  // the bad badge. Somebody asked for it to stop and it stopped; showing that
  // as an error would report the feature working as the feature breaking.
  const badge =
    job.status === 'running'
      ? 'running'
      : job.status === 'completed'
        ? 'ok'
        : job.status === 'cancelled'
          ? 'parked'
          : 'bad'
  return (
    <div className={job.status === 'failed' ? 'error' : 'notice'}>
      <div className="row">
        <span className={`badge ${badge}`}>{job.status}</span>
        <span>job {job.id}</span>
        {job.status === 'running' && (
          <>
            <span className="spinner" />
            <span className="mono">scanning…</span>
          </>
        )}
      </div>
      {job.status === 'completed' && (
        <div style={{ marginTop: 'var(--s2)' }}>
          Matched <strong>{String(detail.matched ?? '?')}</strong> of{' '}
          {String(detail.scanned ?? '?')} accounts scanned — saved as run{' '}
          <strong>{String(detail.run_id ?? '?')}</strong>.
        </div>
      )}
      {job.status === 'cancelled' && (
        <div style={{ marginTop: 'var(--s2)' }}>
          {String(detail.note ?? 'Stopped on request.')}
          <div className="hint caption">
            A cancelled scan saves no list. The CRM connection is free again, so another
            scan can start immediately.
          </div>
        </div>
      )}
      {job.status === 'failed' && (
        <div style={{ marginTop: 'var(--s2)' }}>
          <span className="mono">
            {String(detail.refused ?? detail.error ?? 'failed')}
          </span>
          {detail.note ? <div className="hint caption">{String(detail.note)}</div> : null}
        </div>
      )}
    </div>
  )
}

/**
 * What a scan found, and who else it would reach.
 *
 * Filters are non-exclusive (D84), so every filter's count here is its real
 * population, and `overlap` says how many of this filter's matches also match
 * each other filter — the readers two campaigns would share. Cadence, not
 * order, decides who actually gets mail.
 */
function PreviewResult({ preview }: { preview: RunPreview }) {
  const matchedBy = preview.matched_by ?? {}
  const name = preview.filter ?? preview.filters[0]
  const overlap = (preview.overlap ?? {})[name] ?? {}
  const names = Object.keys(matchedBy).sort((a, b) => (matchedBy[b] ?? 0) - (matchedBy[a] ?? 0))

  return (
    <div className="notice">
      <div>
        <strong>{(preview.matched ?? 0).toLocaleString()}</strong> of{' '}
        {preview.scanned.toLocaleString()} scanned accounts match{' '}
        <span className="mono">{name}</span>. Nothing was saved.
      </div>

      {preview.not_counted?.length > 0 && (
        <div className="hint caption" style={{ marginTop: 'var(--s2)' }}>
          Not counted in this scan (different activity window): {preview.not_counted.join(', ')}.
        </div>
      )}

      <div style={{ marginTop: 'var(--s2)' }}>
        <div className="hint label-line">
          Every filter's population in the same scan. <strong>Also match this one</strong> is
          how many of <span className="mono">{name}</span>'s accounts that filter matches too —
          if both back a campaign, cooldown and the account caps decide who hears from which.
        </div>
        <table style={{ maxWidth: 620 }}>
          <thead>
            <tr>
              <th>Filter</th>
              <th className="num">Matches</th>
              <th className="num">Also match this one</th>
            </tr>
          </thead>
          <tbody>
            {names.map((k) => (
              <tr key={k} className={k === name ? 'selected' : undefined}>
                <td className="mono">{k}</td>
                <td className="num">{(matchedBy[k] ?? 0).toLocaleString()}</td>
                <td className="num dim">{k === name ? '' : (overlap[k] ?? 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
